import React, { type FC, useEffect } from "react";

import { useIntl } from "react-intl";

import { useRecoilCallback, useRecoilValue, useResetRecoilState } from "recoil";

import { EmptyState, Tooltip } from "mailup-web-components";

import Page from "Containers/Page";
import { ModalConfirm, ToastContainer } from "mailup-web-components";
import { useNavigate } from "react-router-dom";
import Header from "./Components/Header";
import ImportedTable from "./Components/ImportedTable";
import ModalClauseImportState from "./Components/ModalClauseImport";
import Search from "./Components/Search";
import ToImportTable from "./Components/ToImportTable";
import {
  deleteImportsCallback,
  pollingImportsCallback,
  resetPageCallback,
  searchImportedCallback,
  searchToImportsCallback,
} from "./recoil/callbacks";
import {
  importStatus,
  isEmptyStateStatus,
  modalDelesteImpostsState,
  tableImportedState,
} from "./recoil/state";

import { gainsightTrack } from "mailup-react-helpers";

export const ImportStatus: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const searchToImports = useRecoilCallback(searchToImportsCallback, []);
  const searchImported = useRecoilCallback(searchImportedCallback, []);
  const resetPage = useRecoilCallback(resetPageCallback, []);

  const isEmptyState = useRecoilValue(isEmptyStateStatus);
  const { totalItems, loading } = useRecoilValue(tableImportedState);
  const { show, title, descr, source, id } = useRecoilValue(
    modalDelesteImpostsState,
  );
  const resetModalDelesteImpostsState = useResetRecoilState(
    modalDelesteImpostsState,
  );
  const deleteImports = useRecoilCallback(deleteImportsCallback);
  const pollingImports = useRecoilCallback(pollingImportsCallback, []);
  const { status } = useRecoilValue(importStatus);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    gainsightTrack("mup_import_visitStatusPage", {});

    initPage();

    return () => {
      resetPage();
    };
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (status === "running") {
      pollingImports(false);
      // Avvia il polling ogni 5 secondi quando lo status è running
      intervalId = setInterval(() => {
        pollingImports(false);
      }, 5000);
    } else if (status === "stop") {
      // Chiama pollingImports(true) quando lo status diventa stop
      pollingImports(true);
    }

    // Cleanup: rimuovi l'intervallo quando il componente viene smontato o lo status cambia
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [status, pollingImports]);

  const initPage = () => {
    searchToImports(true);
    searchImported(true);
  };

  const onClickConfirm = () => {
    deleteImports();
  };

  return (
    <>
      <ToastContainer />
      <ModalClauseImportState />
      <ModalConfirm
        show={show}
        title={title}
        message={descr}
        stringConfirm={intl.formatMessage({
          id: "Load.MailupCommon.Remove",
        })}
        stringCancel={intl.formatMessage({
          id: "Load.MailupCommon.Cancel",
        })}
        variant="delete"
        onClickConfirm={onClickConfirm}
        onClickCancel={resetModalDelesteImpostsState}
      />
      <Tooltip />
      <Header />
      <Page onReload={initPage}>
        {isEmptyState ? (
          <EmptyState
            title={intl.formatMessage({
              id: "Load.FileImportStatus.EmptyStateTitle",
            })}
            buttonText={intl.formatMessage({
              id: "Load.FileImportStatus.EmptyStateCta",
            })}
            description={intl.formatMessage({
              id: "Load.FileImportStatus.EmptyStateDescr",
            })}
            onClickButton={() => {
              navigate("/recipients/import");
            }}
          />
        ) : (
          <>
            <ToImportTable />
            {(totalItems > 0 || loading) && (
              <>
                <Search />
                <ImportedTable />
              </>
            )}
          </>
        )}
      </Page>
    </>
  );
};
